import { useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { formatDateTime } from './patientFormat'
import { editorValue, integerTyping, measureError, normalizeDraft, pressureError, temperatureTyping } from './vitalFields'

const FIELDS = [
  { key: 'heartRate', column: 'heart_rate', label: 'FC' },
  { key: 'oxygenSaturation', column: 'oxygen_saturation', label: 'SpO2' },
  { key: 'temperature', column: 'temperature', label: 'Temp' },
  { key: 'bloodGlucose', column: 'blood_glucose', label: 'Glicemia' },
  { key: 'respiratoryRate', column: 'respiratory_rate', label: 'FR' },
  { key: 'painScale', column: 'pain_scale', label: 'Dor' }
]

function displayValue(item, field) {
  if (field.key === 'heartRate') return item.heart_rate ? `${item.heart_rate} bpm` : '—'
  if (field.key === 'oxygenSaturation') return item.oxygen_saturation ? `${item.oxygen_saturation}%` : '—'
  if (field.key === 'temperature') return item.temperature ? `${item.temperature}°C` : '—'
  if (field.key === 'bloodGlucose') return item.blood_glucose ? `${item.blood_glucose} mg/dL` : '—'
  if (field.key === 'respiratoryRate') return item.respiratory_rate ? `${item.respiratory_rate} rpm` : '—'
  if (item.pain_scale == null || item.pain_scale === '') return '—'
  return `${item.pain_scale}/10`
}

function typeValue(key, raw) {
  if (key === 'temperature') return temperatureTyping(raw)
  return integerTyping(raw, key)
}

function CellEditor({ initial, fieldKey, onCommit, onCancel }) {
  const [value, setValue] = useState(initial)
  const inputRef = useRef(null)
  const cancelRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  return (
    <input
      ref={inputRef}
      className="patients-cell-input"
      value={value}
      inputMode={fieldKey === 'temperature' ? 'decimal' : 'numeric'}
      aria-label="Corrigir valor"
      onChange={(event) => {
        const next = typeValue(fieldKey, event.target.value)
        if (next == null) return
        setValue(next)
      }}
      onBlur={() => {
        if (!cancelRef.current) onCommit(value)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          event.currentTarget.blur()
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          cancelRef.current = true
          onCancel()
        }
      }}
    />
  )
}

function PressureEditor({ systolic, diastolic, onCommit, onCancel }) {
  const [sys, setSys] = useState(systolic)
  const [dia, setDia] = useState(diastolic)
  const boxRef = useRef(null)
  const cancelRef = useRef(false)

  useEffect(() => {
    boxRef.current?.querySelector('input')?.focus()
  }, [])

  const commit = () => {
    if (!cancelRef.current) onCommit(sys, dia)
  }

  return (
    <span
      ref={boxRef}
      className="patients-cell-pair"
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return
        commit()
      }}
    >
      <input
        className="patients-cell-input"
        value={sys}
        inputMode="numeric"
        aria-label="Sistólica"
        onChange={(event) => {
          const next = integerTyping(event.target.value, 'systolic')
          if (next == null) return
          setSys(next)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            cancelRef.current = true
            onCancel()
          }
        }}
      />
      <span>/</span>
      <input
        className="patients-cell-input"
        value={dia}
        inputMode="numeric"
        aria-label="Diastólica"
        onChange={(event) => {
          const next = integerTyping(event.target.value, 'diastolic')
          if (next == null) return
          setDia(next)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            cancelRef.current = true
            onCancel()
          }
        }}
      />
    </span>
  )
}

export default function VitalHistoryTable({ vitals, canEdit, onSave, summary, emptyLabel = 'Nenhuma aferição registrada.', actions = null }) {
  const [editMode, setEditMode] = useState(false)
  const [editing, setEditing] = useState(null)
  const [error, setError] = useState('')

  const closeEditor = () => setEditing(null)

  const saveField = async (item, payload) => {
    const systolic = payload.systolic !== undefined ? normalizeDraft(payload.systolic) : undefined
    const diastolic = payload.diastolic !== undefined ? normalizeDraft(payload.diastolic) : undefined
    const value = payload.value !== undefined ? normalizeDraft(payload.value) : undefined
    const message = payload.systolic !== undefined
      ? pressureError(systolic, diastolic)
      : measureError(payload.field, value)
    if (message) {
      setError(message)
      return
    }
    setError('')
    const body = payload.systolic !== undefined
      ? { systolic, diastolic }
      : { [payload.field]: value }
    try {
      await onSave(item.id, body)
      closeEditor()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível corrigir a aferição')
    }
  }

  return (
    <div className="patients-table-card">
      <div className="patients-table-head">
        <div>
          <h3>Histórico de aferições</h3>
          <p className="patients-muted">
            {editMode
              ? 'Clique no valor para corrigir. Enter salva e Esc cancela.'
              : summary}
          </p>
        </div>
        <div className="patients-section-actions">
          {actions}
          {canEdit && (
          <button
            type="button"
            className={`patients-btn patients-btn--ghost patients-table-edit${editMode ? ' is-on' : ''}`}
            aria-pressed={editMode}
            onClick={() => {
              setEditMode((current) => !current)
              setEditing(null)
              setError('')
            }}
          >
            <Pencil size={14} /> {editMode ? 'Concluir' : 'Editar'}
          </button>
          )}
        </div>
      </div>
      {error && <p className="patients-alert-strip">{error}</p>}
      <div className="patients-table-scroll">
        <table className="patients-table">
          <thead>
            <tr>
              <th>Data / hora</th><th>PA</th><th>FC</th><th>SpO2</th><th>Temp</th><th>Glicemia</th><th>FR</th><th>Dor</th><th>Responsável</th>
            </tr>
          </thead>
          <tbody>
            {vitals.length === 0 && <tr><td colSpan={9}>{emptyLabel}</td></tr>}
            {vitals.map((item) => {
              const pressureOpen = editing?.id === item.id && editing.field === 'pressure'
              return (
                <tr key={item.id}>
                  <td className="is-strong">{formatDateTime(item.recorded_at)}</td>
                  <td className="is-strong">
                    {pressureOpen ? (
                      <PressureEditor
                        systolic={editorValue('systolic', item.systolic)}
                        diastolic={editorValue('diastolic', item.diastolic)}
                        onCancel={closeEditor}
                        onCommit={(systolic, diastolic) => saveField(item, { systolic, diastolic })}
                      />
                    ) : editMode ? (
                      <button type="button" className="patients-cell-edit" onClick={() => { setError(''); setEditing({ id: item.id, field: 'pressure' }) }}>
                        {item.systolic || '—'}/{item.diastolic || '—'}
                      </button>
                    ) : `${item.systolic || '—'}/${item.diastolic || '—'}`}
                  </td>
                  {FIELDS.map((field) => {
                    const open = editing?.id === item.id && editing.field === field.key
                    const className = field.key === 'heartRate' || field.key === 'painScale'
                      ? `tone-${field.key === 'heartRate' ? 'amber' : 'green'} is-strong`
                      : field.key === 'oxygenSaturation'
                        ? 'tone-sky is-strong'
                        : field.key === 'temperature'
                          ? 'tone-rose is-strong'
                          : ''
                    return (
                      <td key={field.key} className={className}>
                        {open ? (
                          <CellEditor
                            fieldKey={field.key}
                            initial={editorValue(field.key, item[field.column])}
                            onCancel={closeEditor}
                            onCommit={(value) => saveField(item, { field: field.key, value })}
                          />
                        ) : editMode ? (
                          <button type="button" className="patients-cell-edit" onClick={() => { setError(''); setEditing({ id: item.id, field: field.key }) }}>
                            {displayValue(item, field)}
                          </button>
                        ) : displayValue(item, field)}
                      </td>
                    )
                  })}
                  <td>
                    <span className="is-strong">{item.recorded_by_name || '—'}</span>
                    {item.notes && <span className="patients-muted" style={{ display: 'block' }}>{item.notes}</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
